/**
 * SRD 5.1 (2014/2021) icerik tanimlari.
 *
 * LİSANS NOTU: Asagidaki sinif/irk ozellik metinleri ve mekanikler D&D 5e
 * SRD 5.1'e dayanir. SRD, Creative Commons Attribution 4.0 (CC BY 4.0)
 * ile lisanslidir; kullanildigi yerde atif (attribution) gosterilmelidir.
 * Uygulamada icerik kaynak ekrani bu atifi yapar.
 *
 * MELEZ NOTU: Kullanici bu oyunda 2024 surumundan gelen "Silah Ustaligi"
 * (Weapon Mastery) sistemini 5.1 siniflariyla birlikte kullaniyor. Bu yuzden
 * Paladin seviye 1'de Weapon Mastery tasir. Diger siniflar eklenirken ayni
 * kural uygulanacak.
 */

import type {
  BackgroundEntry,
  ClassEntry,
  FeatureEntry,
  ItemEntry,
  RaceEntry,
  SpellEntry,
} from '@tttrpg/rules'

// ---------------------------------------------------------------------------
// PALADIN
// ---------------------------------------------------------------------------

const ASI_LEVELS = new Set([4, 8, 12, 16, 19])

/** Tam buyucu yuva tablosu (Paladin = Cleric ile ayni tablo). */
const FULL_CASTER_SLOTS: Record<number, number> = {
  1: 2,
  2: 3,
  3: 3,
  4: 3,
  5: 4,
  6: 4,
  7: 4,
  8: 4,
  9: 5,
  10: 5,
  11: 5,
  12: 5,
  13: 6,
  14: 6,
  15: 6,
  16: 6,
  17: 6,
  18: 6,
  19: 6,
  20: 6,
}

const PALADIN_LEVELS = Array.from({ length: 20 }, (_, i) => i + 1).map((level) => {
  const features: FeatureEntry[] = [
    {
      id: `paladin-divine-sense-${level}`,
      name: 'Tanrısal Algı',
      description: '1 + Karizma modifikatorü kadar Long Rest başına kullanım.',
    },
    {
      id: `paladin-lay-on-hands-${level}`,
      name: 'Yağma',
      description: 'Havuz: 5 × seviye can puanı. Dokunarak iyileştirir.',
    },
    {
      id: `paladin-spellcasting-${level}`,
      name: 'Büyücülük',
      description: 'Büyü yeteneği: Karizma.',
    },
  ]

  if (level === 1) {
    features.push({
      id: 'paladin-weapon-mastery',
      name: 'Silah Ustalığı',
      description:
        'Savunma: zırh giyenken +1 AC. İki El: iki elli silahta +1 isabet. Büyük Silah Dövüşü: hasar zarındaki 1’leri tekrar at.',
      acBonus: 1,
      acBonusCap: 1,
      // 2024 kurali: "zırh giyenken". Zırhsız ırklarda (örn. Ağaç İnsanı)
      // bu bonus UYGULANMAMALI.
      requiresArmor: true,
    })
  }
  if (level === 2) {
    features.push({
      id: 'paladin-fighting-style',
      name: 'Dövüş Biçimi',
      description: 'Koruma, Baskın (Great Weapon Fighting) veya Savaşçı vb. seçilir.',
    })
  }
  if (level === 3) {
    features.push({
      id: 'paladin-divine-smite',
      name: 'İlahi Darbe',
      description: 'Silah saldırısına fazladan kutsal hasar. Seviye arttıkça 2d8 → 5d8.',
    })
    features.push({
      id: 'paladin-aura-of-protection',
      name: 'Kutsal Halka',
      description:
        'Kendine ve yakınındaki müttefiklere Karizma modifikatorü kadar kurtarma atışı bonusu.',
      savingThrowBonuses: { cha: 3 },
    })
  }
  if (level === 5) {
    features.push({
      id: 'paladin-extra-attack',
      name: 'Ek Saldırı',
      description: 'Saldırı eyleminde ek bir saldırı.',
    })
  }
  if (level === 6) {
    features.push({
      id: 'paladin-aura-of-courage',
      name: 'Yüce Halka',
      description: 'Kendine ve yakınındakilere korkuya bağışıklık.',
      conditionImmunities: ['frightened'],
    })
  }
  if (level === 11) {
    features.push({
      id: `paladin-divine-smite-${level}`,
      name: 'İlahi Darbe (Gelişmiş)',
      description: 'İlahi Darbe hasarı +2d8 artar.',
    })
  }

  return {
    level,
    features,
    spellSlots: (FULL_CASTER_SLOTS[level] ? { 1: FULL_CASTER_SLOTS[level]! } : {}) as Record<
      string,
      number
    >,
    asi: ASI_LEVELS.has(level) ? 1 : 0,
    subclassLevel: 3,
  }
})

export const PALADIN: ClassEntry = {
  id: 'paladin',
  name: 'Paladin',
  hitDie: 10,
  primaryAbility: 'cha',
  savingThrowProficiencies: ['wis', 'cha'],
  armorProficiencies: ['light', 'medium', 'heavy', 'shields'],
  weaponProficiencies: ['simple', 'martial'],
  // 2014 PHB Paladin beceri listesi
  skillChoicePool: ['athletics', 'insight', 'intimidation', 'medicine', 'persuasion', 'religion'],
  skillChoices: 2,
  spellcasting: {
    mode: 'full',
    ability: 'cha',
    preparation: 'prepared',
    cantripProgression: [
      { level: 1, count: 2 },
      { level: 4, count: 3 },
      { level: 10, count: 4 },
      { level: 17, count: 5 },
    ],
  },
  levels: PALADIN_LEVELS,
}

// ---------------------------------------------------------------------------
// YÜKSEK ELF
// ---------------------------------------------------------------------------

export const HIGH_ELF: RaceEntry = {
  id: 'high-elf',
  name: 'Yüksek Elf',
  size: 'Orta',
  reach: 5,
  speed: 30,
  abilityBonuses: { dex: 2 },
  // +1 INT/WIS/CHA — oyuncu seçimi. Karakter dosyasında belirlenir.
  asiChoices: {}, // oyuncu +1'i buraya yazar (INT/WIS/CHA)
  asi: 0,
  asiChoiceOptions: ['int', 'wis', 'cha'],
  acBase: 10,
  darkvision: 60,
  cantrips: ['druidcraft'],
  feat: null,
  choices: [],
  innateSpells: [],
  features: [
    {
      id: 'elf-darkvision',
      name: 'Karanlık Görüş',
      description: 'Karanlıkta 60 ft görüş. Karanlık karanlıkta gördüğünü renk olarak görmez.',
    },
    {
      id: 'elf-perception',
      name: 'Dikkatli',
      description: 'Algı (Perception) becerisinde proficiency kazanırsın.',
      // ⚠️ Bu yetkinlik kural metninde AÇIKLAMADA geçiyordu ama veride yoktu.
      // Sihirbaz ve motor yalnızca veriyi okur; açıklama tek başına işe yaramaz.
      grantsProficiencies: ['skill.perception'],
    },
    {
      id: 'elf-fey-ancestry',
      name: 'Peri Soyu',
      description: 'Büyülenme etkilerine karşı kurtarma atışlarında kazanç.',
    },
    {
      id: 'elf-trance',
      name: 'Trans',
      description:
        '8 saatlik dinlenme yerine 4 saat uyku yeterli. Uyanınca tamamen dinlenmiş olursun.',
    },
    {
      id: 'elf-druidcraft',
      name: 'Druidcraft',
      description: 'Druidcraft büyüsünü bilirsin. Büyücülük yeteneği: Zekâ.',
    },
  ],
}

// ---------------------------------------------------------------------------
// ASKER (Soldier)
// ---------------------------------------------------------------------------

export const SOLDIER: BackgroundEntry = {
  id: 'soldier',
  name: 'Asker',
  skillProficiencies: ['skill.athletics', 'skill.intimidation'],
  toolProficiencies: ['tool.gamingSet', 'tool.landVehicles'],
  languages: ['language.common', 'language.dwarven', 'language.halfling'],
  gold: 10,
  features: [
    {
      id: 'soldier-military-rank',
      name: 'Askeri Rütbe',
      description:
        'Orduda bir rütbeye sahipsin. Askerler sana saygı duyar ve senin hizmetinde kalır.',
    },
  ],
}

// ---------------------------------------------------------------------------
// EŞYALAR
// ---------------------------------------------------------------------------

export const ITEMS: ItemEntry[] = [
  {
    id: 'chain-mail',
    name: 'Zincir Zırhı',
    itemType: 'armor',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    armorClass: 16,
    armorBonus: 0,
    armorType: 'heavy',
    dexMode: 'none',
    strengthRequirement: 13,
    stealthDisadvantage: true,
    weight: 55,
  },
  {
    id: 'shield',
    name: 'Kalkan',
    itemType: 'shield',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    shieldBonus: 2,
    weight: 6,
  },
  {
    id: 'longsword',
    name: 'Uzun Kılıç',
    itemType: 'weapon',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    weight: 3,
    weapon: {
      damage: '1d8',
      damageType: 'kesici',
      isVersatile: true,
      versatileDamage: '1d10',
    },
  },
  {
    id: 'spear',
    name: 'Mızrak',
    itemType: 'weapon',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    weight: 2,
    weapon: {
      damage: '1d6',
      damageType: 'delici',
      isVersatile: true,
      versatileDamage: '1d8',
      thrown: true,
    },
  },
  {
    id: 'dagger',
    name: 'Hançer',
    itemType: 'weapon',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    weight: 1,
    weapon: { damage: '1d4', damageType: 'delici', isFinesse: true, thrown: true },
  },
  {
    id: 'explorers-pack',
    name: 'Kaşif Sırtı',
    itemType: 'gear',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    weight: 55,
  },
  {
    id: 'gaming-set',
    name: 'Oyun Takımı',
    itemType: 'gear',
    rarity: 'common',
    requiresAttunement: false,
    attunementMax: 0,
    weight: 0,
  },
]

// ---------------------------------------------------------------------------
// BÜYÜLER
// ---------------------------------------------------------------------------

export const SPELLS: SpellEntry[] = [
  {
    id: 'guidance',
    name: 'Rehberlik (Guidance)',
    level: 0,
    school: 'çevre',
    castingTime: '1 eylem',
    range: 'Kendin veya 30 ft içindeki biri',
    ritual: false,
    concentration: true,
    classes: ['cleric', 'druid'],
  },
  {
    id: 'sacred-flame',
    name: 'Kutsal Alev (Sacred Flame)',
    level: 0,
    school: 'ışık',
    castingTime: '1 eylem',
    range: 'Kendin, 60 ft',
    ritual: false,
    concentration: false,
    classes: ['cleric'],
    damage: {
      type: 'ışık',
      dice: '1d8',
      atHigherLevels: [
        { level: 5, dice: '2d8' },
        { level: 11, dice: '3d8' },
        { level: 17, dice: '4d8' },
      ],
    },
  },
  {
    id: 'druidcraft',
    name: 'Druidcraft',
    level: 0,
    school: 'çevre',
    castingTime: '1 eylem',
    range: '30 ft',
    ritual: false,
    concentration: false,
    classes: ['druid'],
  },
  {
    id: 'bless',
    name: 'Kutsama (Bless)',
    level: 1,
    school: 'evren',
    castingTime: '1 eylem',
    range: '30 ft',
    ritual: false,
    concentration: true,
    classes: ['cleric', 'paladin'],
  },
  {
    id: 'cure-wounds',
    name: 'Sağaltma (Cure Wounds)',
    level: 1,
    school: 'evren',
    castingTime: '1 eylem',
    range: 'Dokunma (30 ft)',
    ritual: false,
    concentration: false,
    classes: ['bard', 'cleric', 'druid', 'paladin', 'ranger'],
    damage: {
      type: 'iyileştirme',
      dice: '1d8',
      atHigherLevels: [
        { level: 2, dice: '2d8' },
        { level: 3, dice: '3d8' },
        { level: 4, dice: '4d8' },
        { level: 5, dice: '5d8' },
        { level: 6, dice: '6d8' },
        { level: 7, dice: '7d8' },
        { level: 8, dice: '8d8' },
        { level: 9, dice: '9d8' },
        { level: 10, dice: '10d8' },
      ],
    },
  },
]
